import { MediaPalette } from '@/classes'

export const shiftColor = (
  swatch?: [number, number, number],
  options?: {
    lightness?: number
    target?: number[]
    hue?: number
    saturation?: number
  }
) => {
  if (!swatch) return (options?.target ?? [255, 255, 255])

  const hsl = rgbToHsl(swatch)

  const hue = hsl[2] < (options?.hue ?? 0) ? 1 : -1
  hsl[0] = (hsl[0] + ((options?.hue ?? 0) * hue)) % 1
  hsl[1] = Math.min(1, hsl[1] + (options?.saturation ?? 0))

  const shiftedRgb = hslToRgb(hsl)
  const lightnessedColor = (options?.target ?? [255, 255, 255]).map((channel, index) => {
    return Math.round(channel + (shiftedRgb[index] - channel) * (options?.lightness ?? 1))
  })

  return lightnessedColor
}

export function rgbToHsl([r, g, b]: number[]): number[] {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }

  return [h ?? 0, s, l];
}

export function hslToRgb([h, s, l]: number[]): number[] {
  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1 / 6) return p + (q - p) * 6 * t;
      if (t < 1 / 2) return q;
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

export function hexToRgb (hex: string): number[] {
  return hex.match(/[A-Za-z0-9]{2}/g)!.map((v) => parseInt(v, 16))
}

export function rgbToHex ([ r, g, b]: number[]) {
  return '#' + [r, g, b].map(x => {
  const hex = x.toString(16)
  return hex.length === 1 ? '0' + hex : hex
}).join('')
} 

export const getEventPalette = (inputPalette: Ref<MediaPalette | undefined | null | any> | ComputedRef<MediaPalette | undefined | null | any>) => {
  const palette = ref(inputPalette.value?.base ?? PALETTES.grey)

  watch(inputPalette, async () => {
    if (inputPalette.value?.base) palette.value = inputPalette.value

    if (!inputPalette.value?.DarkVibrant?.[0]) return

    palette.value = {
      base: {
        bg: shiftColor(inputPalette.value.DarkVibrant ?? [0, 0, 0], {
          lightness: 0.95,
          saturation: -0.15
        }),
        color: shiftColor(inputPalette.value.DarkVibrant ?? [0, 0, 0], {
          lightness: 0.1,
          hue: 0.1,
        })
      },

      accent: {
        bg: shiftColor(inputPalette.value.DarkVibrant ?? [0, 0, 0], {
          lightness: 1.5,
          hue: 0.02,
          saturation: 0.4,
          target: [0, 0, 0]
        }),
        color: shiftColor(inputPalette.value.DarkVibrant ?? [0, 0, 0], {
          lightness: 0.1,
          hue: 0.05
        })
      },

      light: {
        bg: shiftColor(inputPalette.value.DarkVibrant ?? [0, 0, 0], {
          lightness: 0.05,
          hue: 0.05
        }),
        color: shiftColor(inputPalette.value.DarkVibrant ?? [0, 0, 0], {
          lightness: 1,
          hue: 0.05
        })
      }
    }
  }, { immediate: true, deep: true })

  return {
    palette
  }
}

export const PALETTES = {
  purple: { "base": { "bg": [ 73, 40, 123 ], "color": [ 239, 235, 255 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
  blue: { "base": { "bg": [ 32, 45, 100 ], "color": [ 240, 244, 255 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
  grey: { "base": { "bg": [ 56, 59, 71 ], "color": [ 255, 255, 255 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
  pomegranate: { "base": { "bg": [ 164, 40, 58 ], "color": [ 255, 229, 229 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
  pink: { "base": { "bg": [ 169, 81, 150 ], "color": [ 255, 241, 248 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
  aqua: { "base": { "bg": [ 66, 169, 157 ], "color": [ 235, 245, 255 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
  green: { "base": { "bg": [ 76, 144, 96 ], "color": [ 245, 255, 235 ] }, "accent": { "bg": [ 255, 255, 255 ], "color": [ 0, 0, 0 ] }, "light": { "bg": [ 166, 104, 104 ], "color": [ 0, 0, 0 ] } },
}